import {
  JourneyMap,
  JourneyMapDraft,
  JourneyMapDraftItem,
  JourneyMapDraftLink,
  JourneyMapDraftPathGroup,
  JourneyMapDraftPathGroupItem,
} from '../types/journeyMap';

const makeTempId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

function asId(value: any): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value === 'object' && value.id != null) {
    return asId(value.id);
  }
  return null;
}

function asArray<T = any>(value: any): T[] {
  return Array.isArray(value) ? value : [];
}

function normalizeItemType(
  value: string | undefined | null,
): JourneyMapDraftItem['item_type'] {
  if (value === 'section') return 'section';
  if (value === 'assessment') return 'assessment';
  if (value === 'task') return 'task';
  // `checkpoint` and `link` are retired item types; any legacy rows collapse to `content`.
  return 'content';
}

function normalizeLinkConditionType(
  value: string | undefined | null,
): JourneyMapDraftLink['condition_type'] {
  if (value === 'status_equals') return 'status_equals';
  if (value === 'score_gte') return 'score_gte';
  if (value === 'score_gt') return 'score_gt';
  if (value === 'score_lte') return 'score_lte';
  if (value === 'score_lt') return 'score_lt';
  if (value === 'rule') return 'rule';
  return 'always';
}

function normalizeProviderValue(value: string | undefined | null): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const normalized = raw.toLowerCase().replace(/\s+/g, '_');

  if (normalized === 'cyber_academy') return 'cyber_academy';
  if (normalized === 'moodle') return 'moodle';
  if (normalized === 'markdown') return 'markdown';
  if (normalized === 'file') return 'file';
  if (normalized === 'link') return 'link';
  if (normalized === 'journey_map') return 'journey_map';
  if (normalized === 'dcloud') return 'dcloud';

  return normalized;
}

function normalizeLinkType(
  value: string | undefined | null,
): JourneyMapDraftLink['link_type'] {
  if (value === 'pass') return 'pass';
  if (value === 'fail') return 'fail';
  if (value === 'optional') return 'optional';
  if (value === 'skip') return 'skip';
  if (value === 'alternate') return 'alternate';
  if (value === 'conditional') return 'conditional';
  if (value === 'test_out') return 'test_out';
  if (value === 'fallback') return 'fallback';
  return 'default';
}

function normalizePathGroupType(
  value: string | undefined | null,
): JourneyMapDraftPathGroup['group_type'] {
  if (value === 'any_of') return 'any_of';
  if (value === 'one_of') return 'one_of';
  if (value === 'optional') return 'optional';
  if (value === 'test_out') return 'test_out';
  return 'all_of';
}

export function journeyMapToDraft(map: JourneyMap | any): JourneyMapDraft {
  const itemTempIdById: Record<number, string> = {};

  const rawItems = asArray(
    map.journey_map_items ??
    map.items ??
    [],
  );

  const rawLinks = asArray(
    map.item_links ??
    map.journey_map_item_links ??
    map.links ??
    [],
  );

  const rawPathGroups = asArray(
    map.path_groups ??
    map.journey_map_path_groups ??
    [],
  );

  const rawSeparatePathGroupItems = asArray(
    map.path_group_items ??
    map.journey_map_path_group_items ??
    [],
  );

  const items: JourneyMapDraftItem[] = rawItems
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((item, index) => {
      const itemId = asId(item.id);
      const tempId = makeTempId(`journey-map-item-${itemId ?? index + 1}`);

      if (itemId != null) {
        itemTempIdById[itemId] = tempId;
      }

      const metadata = item.metadata || {};

      // dCloud subtitle/name fallback support
      const resolvedReferencedTitle =
        String(
          item?.resolvedReferencedTitle ||
          metadata?.resolvedReferencedTitle ||
          metadata?.dcloud_selected_item?.name ||
          ''
        ).trim();

      return {
        tempId,
        id: itemId,
        journey_map: asId(item.journey_map) ?? asId(map.id),
        name: item.name || '',
        description: item.description || '',
        order: item.order ?? index + 1,
        item_type: normalizeItemType(item.item_type),
        is_required: item.is_required ?? true,
        estimated_duration_minutes: item.estimated_duration_minutes ?? null,
        content_ref_type: item.content_ref_type || '',
        content_ref_id:
          item.content_ref_id != null
            ? String(item.content_ref_id)
            : item.content_ref != null
              ? String(item.content_ref)
              : '',
        provider: normalizeProviderValue(item.provider || ''),
        launch_url: item.launch_url || '',
        completion_mode: item.completion_mode || 'external_sync',
        provider_config: item.provider_config || {},
        prerequisite_logic: item.prerequisite_logic || {},
        metadata: {
          ...metadata,
          resolvedReferencedTitle,
        },
        passing_score: item.passing_score ?? null,
        due_offset_days: item.due_offset_days ?? null,
        available_from: item.available_from || null,
        available_to: item.available_to || null,
        position_x:
          item.position_x ??
          metadata.position_x ??
          240 + ((item.order || index + 1) - 1) * 320,
        position_y:
          item.position_y ??
          metadata.position_y ??
          140,
        isNew: false,
        isDirty: false,
        isDeleted: false,
      };
    });

  const links: JourneyMapDraftLink[] = rawLinks.map((link, index) => {
    const fromItemId = asId(
      link.from_item ??
      link.source_item ??
      link.from_journey_map_item,
    );

    const toItemId = asId(
      link.to_item ??
      link.target_item ??
      link.to_journey_map_item,
    );

    return {
      tempId: makeTempId(`journey-map-link-${link.id ?? index + 1}`),
      id: asId(link.id),
      journey_map: asId(link.journey_map) ?? asId(map.id),
      from_item: fromItemId,
      from_item_temp_id:
        fromItemId != null ? itemTempIdById[fromItemId] || null : null,
      to_item: toItemId,
      to_item_temp_id:
        toItemId != null ? itemTempIdById[toItemId] || null : null,
      label: link.label || '',
      link_type: normalizeLinkType(link.link_type),
      condition_type: normalizeLinkConditionType(link.condition_type),
      condition_config: link.condition_config || {},
      priority: link.priority ?? link.order ?? 0,
      is_active: link.is_active ?? true,
      source_handle: link.source_handle || null,
      target_handle: link.target_handle || null,
      isNew: false,
      isDirty: false,
      isDeleted: false,
    };
  });

  const pathGroups: JourneyMapDraftPathGroup[] = rawPathGroups
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((group, groupIndex) => {
      const groupId = asId(group.id);
      const groupTempId = makeTempId(
        `journey-map-path-group-${groupId ?? groupIndex + 1}`,
      );

      const nestedGroupItems = asArray(
        group.path_group_items ??
        group.items ??
        [],
      );

      const fallbackGroupItems = rawSeparatePathGroupItems.filter((groupItem) => {
        const pathGroupId = asId(groupItem.path_group);
        return groupId != null && pathGroupId === groupId;
      });

      const sourceGroupItems =
        nestedGroupItems.length > 0 ? nestedGroupItems : fallbackGroupItems;

      const groupItems: JourneyMapDraftPathGroupItem[] = sourceGroupItems
        .slice()
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((groupItem, itemIndex) => {
          const journeyMapItemId = asId(
            groupItem.journey_map_item ??
            groupItem.item,
          );

          return {
            tempId: makeTempId(
              `journey-map-path-group-item-${groupItem.id ?? itemIndex + 1}`,
            ),
            id: asId(groupItem.id),
            path_group: asId(groupItem.path_group) ?? groupId,
            path_group_temp_id: groupTempId,
            journey_map_item: journeyMapItemId,
            journey_map_item_temp_id:
              journeyMapItemId != null
                ? itemTempIdById[journeyMapItemId] || null
                : null,
            order: groupItem.order ?? itemIndex + 1,
            isNew: false,
            isDirty: false,
            isDeleted: false,
          };
        });

      return {
        tempId: groupTempId,
        id: groupId,
        journey_map: asId(group.journey_map) ?? asId(map.id),
        name: group.name || '',
        description: group.description || '',
        group_type: normalizePathGroupType(group.group_type),
        min_required: group.min_required ?? 1,
        is_required: group.is_required ?? false,
        order: group.order ?? groupIndex + 1,
        metadata: group.metadata || {},
        items: groupItems,
        isNew: false,
        isDirty: false,
        isDeleted: false,
      };
    });

  return {
    tempId: makeTempId(`journey-map-${map.id || 'new'}`),
    id: asId(map.id),
    name: map.name || 'Untitled Journey Map',
    slug: map.slug || '',
    image_url: map.image_url || '',
    description: map.description || '',
    journey_type: asId(map.journey_type),
    owner: asId(map.owner),
    version: map.version || 1,
    status: map.status || map.publication_status || 'draft',
    publication_status: map.publication_status || map.status || 'draft',
    is_active: map.is_active ?? true,
    estimated_duration_minutes: map.estimated_duration_minutes ?? null,
    audience_rules: map.audience_rules || {},
    metadata: map.metadata || {},
    available_from: map.available_from || null,
    available_to: map.available_to || null,
    items,
    links,
    pathGroups,
    selectedItemTempId: null,
    selectedLinkTempId: null,
    selectedPathGroupTempId: null,
    isDirty: false,
    isNew: false,
  };
}